import { type FC, useCallback, useMemo, useState } from "react";
import { Card } from "../components/atoms/Card";
import LoadingSpinner from "../components/atoms/LoadingSpinner";
import {
  useActivityRecommendations,
  useAddActivityRecommendation,
  useRemoveActivityRecommendation,
  useUpdateRecommendationPriority,
} from "../hooks/activity-recommendations";
import { useCopyLocale } from "../hooks/copy-locale";
import { useAvailableLocales } from "../hooks/data-review";
import { useProducts } from "../hooks/products";
import { LocaleService } from "../services/locale";

export const ActivityRecommendationsPage: FC = () => {
  const locale = LocaleService.getSelectedLocale();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyFromLocale, setCopyFromLocale] = useState("");

  const { data: availableLocales } = useAvailableLocales();
  const {
    data: activities,
    isLoading,
    error,
    refetch,
  } = useActivityRecommendations({
    locale,
  });
  const { data: searchResults, isLoading: searchLoading } = useProducts({
    search: searchTerm,
    perPage: 20,
    locale,
  });

  // Client-side filter for search results
  const filteredSearchResults = useMemo(() => {
    if (!searchResults?.data || !searchFilter.trim()) {
      return searchResults?.data ?? [];
    }

    const lowerFilter = searchFilter.toLowerCase();
    return searchResults.data.filter((product) => {
      return (
        product.name?.toLowerCase().includes(lowerFilter) ||
        product.sku?.toLowerCase().includes(lowerFilter) ||
        product.description?.toLowerCase().includes(lowerFilter) ||
        product.type?.toLowerCase().includes(lowerFilter)
      );
    });
  }, [searchResults, searchFilter]);

  const addRecommendation = useAddActivityRecommendation();
  const removeRecommendation = useRemoveActivityRecommendation();
  const updatePriority = useUpdateRecommendationPriority();
  const copyLocale = useCopyLocale();

  const handleCopyFromLocale = useCallback(() => {
    if (!copyFromLocale || !locale) {
      return;
    }

    if (
      confirm(
        `Copy all recommendations from ${copyFromLocale} to ${locale}? This will overwrite existing recommendations in ${locale}.`
      )
    ) {
      copyLocale.mutate(
        {
          fromLocale: copyFromLocale,
          toLocale: locale,
        },
        {
          onSuccess: (data) => {
            alert(`Successfully copied ${data.copied} recommendations`);
            setShowCopyModal(false);
            setCopyFromLocale("");
            void refetch();
          },
        }
      );
    }
  }, [copyFromLocale, locale, copyLocale, refetch]);

  const handleAddProduct = useCallback(
    (activityName: string, sku: string, addFamily: boolean) => {
      if (!locale) {
        return;
      }

      if (!addFamily) {
        // Add single SKU
        const currentActivity = activities?.find(
          (a) => a.activityName === activityName
        );
        const nextPriority =
          (currentActivity?.recommendations?.length ?? 0) + 1;

        addRecommendation.mutate(
          {
            activityName,
            sku,
            priority: nextPriority,
            locale,
          },
          {
            onSuccess: () => {
              setSearchTerm("");
              setShowProductSearch(false);
              void refetch();
            },
          }
        );
      } else {
        // Add all variants in family
        const product = searchResults?.data?.find((p) =>
          p.variants?.some((v) => v.sku === sku)
        );
        if (product?.variants) {
          const currentActivity = activities?.find(
            (a) => a.activityName === activityName
          );
          let priority = (currentActivity?.recommendations?.length ?? 0) + 1;

          product.variants.forEach((variant) => {
            addRecommendation.mutate({
              activityName,
              sku: variant.sku,
              priority: priority++,

              locale,
            });
          });

          setSearchTerm("");
          setShowProductSearch(false);
          setTimeout(() => void refetch(), 500);
        }
      }
    },
    [activities, searchResults, addRecommendation, refetch, locale]
  );

  const handleRemoveProduct = useCallback(
    (activityName: string, sku: string) => {
      if (!locale) {
        return;
      }

      removeRecommendation.mutate(
        {
          activityName,
          sku,

          locale,
        },
        {
          onSuccess: () => void refetch(),
        }
      );
    },
    [removeRecommendation, refetch, locale]
  );

  const handleMovePriority = useCallback(
    (activityName: string, sku: string, direction: "up" | "down") => {
      if (!locale) {
        return;
      }

      const activity = activities?.find((a) => a.activityName === activityName);
      if (!activity) {
        return;
      }

      const currentIndex = activity.recommendations.findIndex(
        (r) => r.sku === sku
      );
      if (currentIndex === -1) {
        return;
      }

      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= activity.recommendations.length) {
        return;
      }

      updatePriority.mutate(
        {
          activityName,
          sku,
          newPriority: activity.recommendations.at(newIndex)!.priority,

          locale,
        },
        {
          onSuccess: () => void refetch(),
        }
      );
    },
    [activities, updatePriority, refetch, locale]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Activity Product Recommendations
          </h1>
          <p className="text-gray-600 mt-2">
            Manage product recommendations by activity for different locales
          </p>
        </div>

        {/* Locale Selector */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-md">Locale: {locale}</div>
            <button
              onClick={() => setShowCopyModal(true)}
              disabled={!locale}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Copy from Locale
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p className="font-medium">Error loading activities</p>
            <p className="text-sm mt-1">
              {error instanceof Error ? error.message : "Unknown error"}
            </p>
          </div>
        )}

        {!isLoading && !error && activities && locale && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activities.map((activity) => (
              <Card key={activity.activityName} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {activity.activityName}
                  </h2>
                  <button
                    onClick={() => {
                      setSelectedActivity(activity.activityName);
                      setShowProductSearch(true);
                      setSearchTerm("");
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    + Add Product
                  </button>
                </div>

                {/* Current Products */}
                {activity.recommendations.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No products assigned to this activity yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {activity.recommendations
                      .sort((a, b) => a.priority - b.priority)
                      .map((rec, index) => {
                        const product = activity.products.find(
                          (p) => p.sku === rec.sku
                        );
                        return (
                          <div
                            key={rec.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-sm font-medium text-gray-500 w-8">
                                #{rec.priority}
                              </span>
                              {product?.imageUrl ? (
                                <img
                                  src={product.imageUrl}
                                  alt={product.name}
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
                                  <svg
                                    className="w-8 h-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {product?.name ?? rec.sku}
                                </p>
                                <p className="text-sm text-gray-600">
                                  SKU: {rec.sku}
                                </p>
                                {product?.description && (
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                    {product.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  handleMovePriority(
                                    activity.activityName,
                                    rec.sku,
                                    "up"
                                  )
                                }
                                disabled={
                                  index === 0 || updatePriority.isPending
                                }
                                className="p-1 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move up"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 15l7-7 7 7"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() =>
                                  handleMovePriority(
                                    activity.activityName,
                                    rec.sku,
                                    "down"
                                  )
                                }
                                disabled={
                                  index ===
                                    activity.recommendations.length - 1 ||
                                  updatePriority.isPending
                                }
                                className="p-1 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move down"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() =>
                                  handleRemoveProduct(
                                    activity.activityName,
                                    rec.sku
                                  )
                                }
                                disabled={removeRecommendation.isPending}
                                className="p-1 text-red-600 hover:text-red-700 disabled:opacity-30"
                                title="Remove"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Copy Locale Modal */}
        {showCopyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Copy Recommendations
                </h3>
                <button
                  onClick={() => {
                    setShowCopyModal(false);
                    setCopyFromLocale("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Copy all activity recommendations from another locale to{" "}
                  <strong>{locale}</strong>. This will replace all existing
                  recommendations in {locale}.
                </p>

                <label
                  htmlFor="copyFromLocale"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Source Locale
                </label>
                <select
                  id="copyFromLocale"
                  value={copyFromLocale}
                  onChange={(e) => setCopyFromLocale(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select source locale...</option>
                  {availableLocales
                    ?.filter((locale) => locale !== locale)
                    .map((locale) => (
                      <option key={locale} value={locale}>
                        {locale}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCopyModal(false);
                    setCopyFromLocale("");
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCopyFromLocale}
                  disabled={!copyFromLocale || copyLocale.isPending}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copyLocale.isPending ? "Copying..." : "Copy"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Product Search Modal */}
        {showProductSearch && selectedActivity && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Product to {selectedActivity}
                </h3>
                <button
                  onClick={() => {
                    setShowProductSearch(false);
                    setSelectedActivity(null);
                    setSearchTerm("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchFilter}
                    onChange={(e) => {
                      setSearchFilter(e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    placeholder="Search products by name, SKU, description..."
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  {searchLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg
                        className="animate-spin h-5 w-5 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Search Results */}
              {searchLoading && !searchResults && (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              )}

              {!searchLoading && filteredSearchResults && (
                <div className="space-y-2">
                  {filteredSearchResults.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      {searchFilter.trim()
                        ? "No products match your search"
                        : "Start typing to search products"}
                    </p>
                  ) : (
                    filteredSearchResults.map((product) => (
                      <div
                        key={product.id}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="w-20 h-20 object-cover rounded border border-gray-200"
                              />
                            ) : (
                              <div className="w-20 h-20 bg-gray-200 rounded border border-gray-300 flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="w-10 h-10 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {product.name}
                              </p>
                              <p className="text-sm text-gray-600">
                                SKU: {product.sku}
                              </p>
                              {product.description && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                  {product.description}
                                </p>
                              )}
                              {product.variants &&
                                product.variants.length > 1 && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    {product.variants.length} variants in family
                                  </p>
                                )}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                handleAddProduct(
                                  selectedActivity,
                                  product.sku,
                                  false
                                )
                              }
                              disabled={addRecommendation.isPending}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              Add SKU
                            </button>
                            {product.variants &&
                              product.variants.length > 1 && (
                                <button
                                  onClick={() =>
                                    handleAddProduct(
                                      selectedActivity,
                                      product.sku,
                                      true
                                    )
                                  }
                                  disabled={addRecommendation.isPending}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                                >
                                  Add Family ({product.variants.length})
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
